import React from 'react';
import type { AeEvaluatedQuestion, AeQuestionDisplayConfig } from '@/types/assessmentEngine';
import {
  AE_EDITABLE_INPUT,
  AE_INPUT,
  AE_INPUT_COMPACT,
  AE_LABEL,
  AE_LABEL_COMPACT,
  AE_REQUIRED,
  AE_SELECT,
} from '@/components/assessment-engine/layout/aeTheme';
import { InlineEditable } from '@/components/assessment-engine/template-builder/InlineEditable';
import { ScaleInput } from '@/components/assessment-engine/template-builder/ScaleInput';
import { resolveScaleEndpointLabels } from '@/components/assessment-engine/template-builder/scaleUtils';
import {
  parseDisplayConfig,
  toBuilderQuestionType,
} from '@/components/assessment-engine/template-builder/questionTypeConfig';
export interface QuestionAnswerValue {
  answer_text?: string | null;
  answer_numeric?: number | null;
  is_na?: boolean;
  answer_values?: string[] | null;
  /**
   * Matrix: row option_value -> selected scale value, or the 'NA' sentinel when
   * the respondent marks that individual statement (row) as not applicable.
   * N/A is per-row for matrix questions, unlike the question-level `is_na`.
   */
  answer_matrix?: Record<string, number | 'NA'> | null;
}

export interface QuestionBuilderCallbacks {
  onQuestionTextChange?: (text: string) => void;
  onOptionLabelChange?: (index: number, label: string) => void;
  onOptionAdd?: () => void;
  onOptionRemove?: (index: number) => void;
}

interface QuestionRendererProps {
  question: AeEvaluatedQuestion & {
    display_config?: AeQuestionDisplayConfig | Record<string, unknown> | null;
  };
  value: QuestionAnswerValue;
  onChange: (value: QuestionAnswerValue) => void;
  previewMode?: boolean;
  /** In-situ builder: edit question text and choices where respondents see them. */
  builderMode?: boolean;
  builderCallbacks?: QuestionBuilderCallbacks;
  /** Tighter labels/inputs for About you / demographics grids. */
  compact?: boolean;
}

function resolveDisplay(question: QuestionRendererProps['question']): AeQuestionDisplayConfig {
  return parseDisplayConfig(question.display_config);
}

function RequiredIndicator({ required }: { required: boolean }) {
  if (!required) return null;
  return (
    <span className={`${AE_REQUIRED} shrink-0 text-base font-medium leading-none pt-0.5`} aria-hidden>
      *
    </span>
  );
}

function QuestionPrompt({
  text,
  required,
  builderMode,
  onChange,
}: {
  text: string;
  required: boolean;
  builderMode?: boolean;
  onChange?: (t: string) => void;
}) {
  const showRequired = required && !builderMode;
  if (builderMode && onChange) {
    return (
      <legend className="block w-full max-w-none mb-2 text-sm font-medium text-[var(--color-surface-text)]">
        <InlineEditable variant="legend" value={text} onChange={onChange} placeholder="Question text" />
      </legend>
    );
  }
  return (
    <legend className="flex w-full max-w-none items-start gap-1 mb-2 text-sm font-medium text-[var(--color-surface-text)]">
      <RequiredIndicator required={showRequired} />
      <span className="flex-1 min-w-0 whitespace-pre-wrap">{text}</span>
    </legend>
  );
}

function OptionLabel({
  label,
  builderMode,
  onChange,
}: {
  label: string;
  builderMode?: boolean;
  onChange?: (v: string) => void;
}) {
  if (builderMode && onChange) {
    return (
      <input
        className={`${AE_EDITABLE_INPUT} text-sm flex-1 min-w-0`}
        value={label}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }
  return <span>{label}</span>;
}

/**
 * Renders assessment questions for take flow and in-situ builder editing.
 */
export const QuestionRenderer: React.FC<QuestionRendererProps> = ({
  question,
  value,
  onChange,
  previewMode,
  builderMode,
  builderCallbacks,
  compact,
}) => {
  const labelClass = compact ? AE_LABEL_COMPACT : AE_LABEL;
  const inputClass = compact ? AE_INPUT_COMPACT : AE_INPUT;
  const builderType = toBuilderQuestionType(question.question_type);
  const display = resolveDisplay(question);
  const disabled = previewMode && !builderMode;
  const cb = builderCallbacks;

  if (builderType === 'nps') {
    return (
      <fieldset className="w-full min-w-0 border-0 p-0 m-0">
        <QuestionPrompt
          text={question.question_text}
          required={question.is_required}
          builderMode={builderMode}
          onChange={cb?.onQuestionTextChange}
        />
        <ScaleInput
          min={0}
          max={10}
          name={`scale-${question.id}`}
          display={display}
          value={value.answer_numeric}
          isNa={value.is_na}
          allowNa={question.allow_na}
          disabled={disabled}
          onChange={(n, na) => onChange({ answer_numeric: n, is_na: na })}
        />
      </fieldset>
    );
  }

  if (builderType === 'matrix') {
    const min = question.scale_min ?? 1;
    const max = question.scale_max ?? 5;
    const matrixVal = value.answer_matrix ?? {};
    const valueColumns = Array.from({ length: max - min + 1 }, (_, i) => min + i);
    const endpoints = resolveScaleEndpointLabels(display);
    // When per-value labels are defined they already label every column, so the
    // Low/High endpoint header row is redundant and hidden.
    const hasCustomValueLabels = (display.scaleValueLabels ?? []).some(
      (label) => (label ?? '').trim().length > 0
    );
    // N/A is a per-row (per-statement) option for matrix questions: each row can
    // independently be marked not applicable, so it renders as an extra column.
    const showNa = question.allow_na === true;
    // Matches the scale input's N/A affordance so builder/preview/take stay consistent.
    const naLabel = 'N/A';
    return (
      <fieldset className="w-full min-w-0 border-0 p-0 m-0">
        <QuestionPrompt
          text={question.question_text}
          required={question.is_required}
          builderMode={builderMode}
          onChange={cb?.onQuestionTextChange}
        />
        <div className="mt-3 w-full min-w-0 overflow-x-auto">
          <table className="w-full min-w-0 table-fixed text-sm border-collapse">
            <colgroup>
              <col className="w-1/3" />
              {valueColumns.map((n) => (
                // With the row-label column fixed, table-layout: fixed divides
                // the remaining width equally across every matrix choice.
                <col key={n} data-matrix-choice-column={n} />
              ))}
              {showNa && <col data-matrix-na-column className="w-16" />}
            </colgroup>
            <thead>
              {!hasCustomValueLabels && (
                <tr>
                  <th className="text-left p-2 border-b border-[var(--color-border)]" />
                  {valueColumns.map((n, i) => (
                    <th
                      key={`label-${n}`}
                      className="whitespace-normal break-words p-2 border-b border-[var(--color-border)] text-center text-xs font-normal leading-tight text-[var(--color-secondary-text)]"
                      data-matrix-choice-label={n}
                    >
                      {i === 0 ? endpoints.scaleMinLabel : i === valueColumns.length - 1 ? endpoints.scaleMaxLabel : ''}
                    </th>
                  ))}
                  {showNa && <th className="p-2 border-b border-[var(--color-border)]" />}
                </tr>
              )}
              <tr>
                <th className="text-left p-2 border-b border-[var(--color-border)]" />
                {valueColumns.map((n, i) => (
                  <th
                    key={n}
                    className="whitespace-normal break-words p-2 border-b border-[var(--color-border)] text-center text-xs font-normal leading-tight text-[var(--color-secondary-text)]"
                  >
                    {display.scaleValueLabels?.[i] ?? n}
                  </th>
                ))}
                {showNa && (
                  <th className="whitespace-normal break-words p-2 border-b border-[var(--color-border)] text-center text-xs font-normal leading-tight text-[var(--color-secondary-text)]">
                    {naLabel}
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {question.options.map((opt, rowIndex) => (
                <tr key={opt.id} className="border-b border-[var(--color-border)]">
                  <td className="p-2 align-middle">
                    <div className="flex items-center gap-1">
                      <OptionLabel
                        label={opt.option_label}
                        builderMode={builderMode}
                        onChange={(v) => cb?.onOptionLabelChange?.(rowIndex, v)}
                      />
                      {builderMode && question.options.length > 1 && (
                        <button
                          type="button"
                          className="text-xs text-[var(--color-error)]"
                          onClick={() => cb?.onOptionRemove?.(rowIndex)}
                        >
                          ×
                        </button>
                      )}
                    </div>
                  </td>
                  {valueColumns.map((n) => (
                    <td key={n} className="p-2 text-center">
                      <input
                        type="radio"
                        name={`matrix-${question.id}-${opt.option_value}`}
                        aria-label={`${opt.option_label}: ${display.scaleValueLabels?.[n - min] ?? n}`}
                        disabled={disabled}
                        checked={matrixVal[opt.option_value] === n}
                        onChange={() =>
                          onChange({
                            answer_matrix: { ...matrixVal, [opt.option_value]: n },
                            is_na: false,
                          })
                        }
                        className="accent-[var(--color-accent-bg)]"
                      />
                    </td>
                  ))}
                  {showNa && (
                    <td className="p-2 text-center" data-matrix-na-cell>
                      <input
                        type="radio"
                        name={`matrix-${question.id}-${opt.option_value}`}
                        aria-label={`${opt.option_label}: ${naLabel}`}
                        disabled={disabled}
                        checked={matrixVal[opt.option_value] === 'NA'}
                        onChange={() =>
                          onChange({
                            answer_matrix: { ...matrixVal, [opt.option_value]: 'NA' },
                            is_na: false,
                          })
                        }
                        className="accent-[var(--color-accent-bg)]"
                      />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          {builderMode && cb?.onOptionAdd && (
            <button
              type="button"
              className="mt-2 text-sm text-[var(--color-info)] hover:underline"
              onClick={cb.onOptionAdd}
            >
              + Add statement
            </button>
          )}
        </div>
      </fieldset>
    );
  }

  if (builderType === 'scale') {
    const min = question.scale_min ?? 1;
    const max = question.scale_max ?? 5;
    return (
      <fieldset className="w-full min-w-0 border-0 p-0 m-0">
        <QuestionPrompt
          text={question.question_text}
          required={question.is_required}
          builderMode={builderMode}
          onChange={cb?.onQuestionTextChange}
        />
        <ScaleInput
          min={min}
          max={max}
          name={`scale-${question.id}`}
          display={display}
          value={value.answer_numeric}
          isNa={value.is_na}
          allowNa={question.allow_na}
          disabled={disabled}
          onChange={(n, na) => onChange({ answer_numeric: n, is_na: na })}
        />
      </fieldset>
    );
  }

  if (builderType === 'single_choice') {
    const mode = display.choiceDisplay ?? 'radio';
    const selected = value.answer_text;

    if (mode === 'dropdown' && !builderMode) {
      return (
        <div className="w-full min-w-0">
          <div className="flex w-full items-start gap-1">
            <RequiredIndicator required={question.is_required && !builderMode} />
            <label className={`flex-1 min-w-0 ${labelClass}`} htmlFor={question.id}>
              {question.question_text}
            </label>
          </div>
          <select
            id={question.id}
            disabled={disabled}
            className={`${compact ? 'mt-0.5' : 'mt-1'} ${compact ? AE_INPUT_COMPACT : AE_SELECT}`}
            value={value.answer_text ?? ''}
            onChange={(e) => onChange({ answer_text: e.target.value || null, is_na: false })}
          >
            <option value="">—</option>
            {question.options.map((opt) => (
              <option key={opt.id} value={opt.option_value}>
                {opt.option_label}
              </option>
            ))}
          </select>
        </div>
      );
    }

    return (
      <fieldset className="w-full min-w-0 border-0 p-0 m-0">
        <QuestionPrompt
          text={question.question_text}
          required={question.is_required}
          builderMode={builderMode}
          onChange={cb?.onQuestionTextChange}
        />
        <ul className="mt-2 space-y-2 w-full">
          {question.options.map((opt, i) => (
            <li key={opt.id} className="flex items-start gap-2 w-full min-w-0">
              <input
                type={mode === 'checkbox' && !builderMode ? 'checkbox' : 'radio'}
                name={question.id}
                aria-label={opt.option_label}
                disabled={disabled}
                checked={selected === opt.option_value}
                onChange={() => onChange({ answer_text: opt.option_value, is_na: false })}
                className="accent-[var(--color-accent-bg)] shrink-0"
              />
              <OptionLabel
                label={opt.option_label}
                builderMode={builderMode}
                onChange={(v) => cb?.onOptionLabelChange?.(i, v)}
              />
              {builderMode && question.options.length > 1 && (
                <button
                  type="button"
                  className="text-xs text-[var(--color-error)] shrink-0"
                  onClick={() => cb?.onOptionRemove?.(i)}
                >
                  ×
                </button>
              )}
            </li>
          ))}
        </ul>
        {builderMode && cb?.onOptionAdd && (
          <button type="button" className="mt-2 text-sm text-[var(--color-info)] hover:underline" onClick={cb.onOptionAdd}>
            + Add choice
          </button>
        )}
      </fieldset>
    );
  }

  if (builderType === 'multi_choice') {
    const mode = display.choiceDisplay ?? 'checkbox';
    const selectedSet = new Set(value.answer_values ?? []);
    const toggle = (optValue: string, checked: boolean) => {
      const next = new Set(selectedSet);
      if (checked) next.add(optValue);
      else next.delete(optValue);
      onChange({ answer_values: Array.from(next), is_na: false });
    };

    if (mode === 'boxes' && !builderMode) {
      return (
        <fieldset className="w-full min-w-0 border-0 p-0 m-0">
          <QuestionPrompt text={question.question_text} required={question.is_required} />
          <div className="flex flex-wrap gap-2 mt-2">
            {question.options.map((opt) => {
              const on = selectedSet.has(opt.option_value);
              return (
                <button
                  key={opt.id}
                  type="button"
                  disabled={disabled}
                  className={
                    on
                      ? 'px-3 py-2 rounded border text-sm bg-[var(--color-accent-bg)] text-[var(--color-accent-text)]'
                      : 'px-3 py-2 rounded border text-sm border-[var(--color-border)]'
                  }
                  onClick={() => toggle(opt.option_value, !on)}
                >
                  {opt.option_label}
                </button>
              );
            })}
          </div>
        </fieldset>
      );
    }

    return (
      <fieldset className="w-full min-w-0 border-0 p-0 m-0">
        <QuestionPrompt
          text={question.question_text}
          required={question.is_required}
          builderMode={builderMode}
          onChange={cb?.onQuestionTextChange}
        />
        <ul className="mt-2 space-y-2 w-full">
          {question.options.map((opt, i) => (
            <li key={opt.id} className="flex items-start gap-2 w-full min-w-0">
              <input
                type="checkbox"
                disabled={disabled}
                checked={selectedSet.has(opt.option_value)}
                onChange={(e) => toggle(opt.option_value, e.target.checked)}
                className="accent-[var(--color-accent-bg)] shrink-0"
              />
              <OptionLabel
                label={opt.option_label}
                builderMode={builderMode}
                onChange={(v) => cb?.onOptionLabelChange?.(i, v)}
              />
              {builderMode && question.options.length > 1 && (
                <button
                  type="button"
                  className="text-xs text-[var(--color-error)]"
                  onClick={() => cb?.onOptionRemove?.(i)}
                >
                  ×
                </button>
              )}
            </li>
          ))}
        </ul>
        {builderMode && cb?.onOptionAdd && (
          <button type="button" className="mt-2 text-sm text-[var(--color-info)] hover:underline" onClick={cb.onOptionAdd}>
            + Add choice
          </button>
        )}
      </fieldset>
    );
  }

  const isLong = builderType === 'long_text';
  if (builderMode && cb?.onQuestionTextChange) {
    return (
      <div className="w-full min-w-0">
        <InlineEditable
          variant="legend"
          value={question.question_text}
          onChange={cb.onQuestionTextChange}
          placeholder="Question text"
        />
        {!isLong && (
          <input className={`mt-2 w-full ${AE_INPUT}`} disabled placeholder="Respondent answer" readOnly />
        )}
      </div>
    );
  }

  const InputTag = isLong ? 'textarea' : 'input';
  return (
    <div className="w-full min-w-0">
      <div className="flex w-full items-start gap-1">
        <RequiredIndicator required={question.is_required && !builderMode} />
        <label className={`flex-1 min-w-0 ${labelClass}`} htmlFor={question.id}>
          {question.question_text}
        </label>
      </div>
      <InputTag
        id={question.id}
        disabled={disabled}
        className={`${compact ? 'mt-0.5' : 'mt-1'} ${inputClass} ${isLong ? 'min-h-[2.5rem]' : 'min-h-0'}`}
        rows={isLong ? (compact ? 3 : 4) : undefined}
        value={value.answer_text ?? ''}
        onChange={(e) => onChange({ answer_text: e.target.value, is_na: false })}
      />
    </div>
  );
};
